import type {CopilotChatModel, CopilotModel} from '@github-ui/copilot-chat-types'
import {FIRST_PARTY_VENDORS} from '@github-ui/copilot-chat-state/model-vendors'
import {getEnv} from '@github-ui/client-env'
import {useEffect, useEffectEvent, useState} from 'react'
import {captureUiReadinessTime} from '@github-ui/copilot-browser-latency/copilot-ui-readiness'
import {useCopilotUiReadiness} from '@github-ui/copilot-browser-latency/copilot-ui-readiness-context'
import {fetchCAPI} from '../capi/fetch-capi'
import {getDisplayName} from './model-display-name'

// Logo URLs for different model vendors
const OPEN_AI_LOGO = '/images/modules/marketplace/models/families/openai-logo.svg'
const ANTHROPIC_LOGO = '/images/modules/marketplace/models/families/anthropic.svg'
const GEMINI_LOGO = '/images/modules/marketplace/models/families/gemini.svg'
const XAI_LOGO = '/images/modules/marketplace/models/families/xai.svg'
const XAI_LOGO_DARK = '/images/modules/marketplace/models/families/xai-dark.svg'
const MICROSOFT_LOGO = '/images/modules/marketplace/models/families/microsoft.svg'
const DEEPSEEK_LOGO = '/images/modules/marketplace/models/families/deepseek.svg'
const MOONSHOT_AI_LOGO = '/images/modules/marketplace/models/families/moonshot-ai.svg'

const LOGO_BY_VENDOR = new Map([
  ['Azure OpenAI', OPEN_AI_LOGO],
  ['OpenAI', OPEN_AI_LOGO],
  ['Anthropic', ANTHROPIC_LOGO],
  ['Google', GEMINI_LOGO],
  ['xAI', XAI_LOGO],
  ['Microsoft', MICROSOFT_LOGO],
  ['DeepSeek', DEEPSEEK_LOGO],
  ['Moonshot AI', MOONSHOT_AI_LOGO],
])

export {LOGO_BY_VENDOR}

export type AgentModelCatalogScope =
  | {actorType: 'user'; repoId?: number}
  | {actorType: 'org'; ownerId: number; repoId: number}

type ModelCatalog = {
  models: CopilotChatModel[]
  defaultModelId: string | undefined
  readinessTime: ReturnType<typeof captureUiReadinessTime>
}

type ModelCacheEntry = {
  catalog?: ModelCatalog
  promise?: Promise<ModelCatalog>
}

const modelCache = new Map<string, ModelCacheEntry>()
const EMPTY_MODELS: CopilotChatModel[] = []
const MISSING_SCOPE_ERROR = new Error(
  'Repository ownership details are unavailable. Refresh the page to load models for this automation.',
)

/** Test-only: clears the module-level model cache so cases start from a clean fetch. */
export function resetAgentModelsCacheForTesting() {
  modelCache.clear()
}

/** Omit scope for the authenticated user; null fails closed when a required identity is unavailable. */
export function useAgentModels(scope?: AgentModelCatalogScope | null): {
  models: CopilotChatModel[]
  isLoading: boolean
  error: Error | null
  defaultModelId?: string
} {
  const endpoint = modelCatalogPath(scope)
  const {login, copilotApiOverrideUrl} = getEnv()
  const cacheKey = JSON.stringify([login, copilotApiOverrideUrl, scope?.repoId, endpoint])
  const [state, setState] = useState<{cacheKey: string; catalog?: ModelCatalog; error: Error | null}>(() => ({
    cacheKey,
    catalog: modelCache.get(cacheKey)?.catalog,
    error: null,
  }))
  // Never expose the previous identity's data, even for the render before effect cleanup.
  const catalog = state.cacheKey === cacheKey ? state.catalog : modelCache.get(cacheKey)?.catalog
  const error = endpoint === null ? MISSING_SCOPE_ERROR : state.cacheKey === cacheKey ? state.error : null
  const readiness = useCopilotUiReadiness()
  const observeModelReadiness = useEffectEvent(() => {
    if (catalog) readiness?.configurationAvailable(catalog.readinessTime ?? readiness.startedAt)
  })

  useEffect(() => {
    if (endpoint === null) return
    let active = true
    let entry = modelCache.get(cacheKey)
    if (!entry) {
      entry = {}
      modelCache.set(cacheKey, entry)
    }

    if (entry.catalog) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState({cacheKey, catalog: entry.catalog, error: null})
      return
    }

    setState({cacheKey, error: null})
    const cacheEntry = entry
    cacheEntry.promise ??= (async () => {
      try {
        const result = await fetchModels(endpoint)
        cacheEntry.catalog = result
        return result
      } finally {
        cacheEntry.promise = undefined
      }
    })()
    const pending = cacheEntry.promise

    void (async () => {
      try {
        const result = await pending
        if (active) setState({cacheKey, catalog: result, error: null})
      } catch (err) {
        if (active) {
          setState({
            cacheKey,
            error: err instanceof Error ? err : new Error('An unexpected error occurred while loading models.'),
          })
        }
      }
    })()
    return () => {
      active = false
    }
  }, [cacheKey, endpoint])

  useEffect(() => {
    observeModelReadiness()
  }, [catalog, readiness])

  return {
    models: catalog?.models ?? EMPTY_MODELS,
    isLoading: !catalog && !error,
    error,
    defaultModelId: catalog?.defaultModelId,
  }
}

function modelCatalogPath(scope: AgentModelCatalogScope | null | undefined): string | null {
  if (scope === null) return null
  const endpoint = '/agents/swe/models'
  if (scope?.actorType !== 'org') return endpoint
  if (
    !Number.isSafeInteger(scope.ownerId) ||
    scope.ownerId <= 0 ||
    !Number.isSafeInteger(scope.repoId) ||
    scope.repoId <= 0
  ) {
    return null
  }
  const params = new URLSearchParams({
    actor_type: 'org',
    owner_id: String(scope.ownerId),
    repo_id: String(scope.repoId),
  })
  return `${endpoint}?${params}`
}

async function fetchModels(endpoint: string): Promise<ModelCatalog> {
  const response = await fetchCAPI({
    path: endpoint,
    method: 'GET',
    useCapi: true,
  })
  const data: {
    data?: Array<CopilotModel & {capabilities: {type: 'chat'}}>
    default_model?: string
  } = await response.json()
  const readinessTime = captureUiReadinessTime()
  const isDarkMode = document.querySelector('[data-color-mode="dark"]') !== null
  const models = (data.data || []).map(model => {
    let logoUrl = LOGO_BY_VENDOR.get(model.vendor)
    if (model.vendor === 'xAI' && isDarkMode) logoUrl = XAI_LOGO_DARK
    return {
      ...model,
      displayName: getDisplayName(model.name, model.id),
      logoURL: logoUrl,
      hasLimitedCapabilities: !model.capabilities.supports.tool_calls,
      isThirdParty: !FIRST_PARTY_VENDORS.has(model.vendor),
    }
  })
  // Enterprise managed settings provide a default hint, not a lock.
  const defaultModelId = typeof data.default_model === 'string' ? data.default_model : undefined
  return {models, defaultModelId, readinessTime}
}
